import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LocationServiceDto, InTransitDto, JourneyItemDto, CustomerLocationDto, ActiveDashboardResponse, AssetLocationService, CustomerAssetLocationModel, CurrentAssetLocationDto, LocationTypeClassification, AssetTypeClassification, LocationTypeDto, AssetTypeGroupDto, ContainerAvailabilityQuantityDto } from 'src/shared/service-proxies/service-proxies';
import { PageHelperService } from '../helpers/page-helper.service';
import { PagebaseComponent } from '../pagebase/pagebase.component';
import { ApplicationStatusService } from '../services/application-status.service';
import moment from 'moment';
import { SummaryDataItemModel } from '../models/summary-data-item-model';
import { SessionStorageService } from '../services/session-storage.service';
import { LocationClassNameHelper, LocationTypeNames } from '../enums/LocationTypeNames';
import { Router } from '@angular/router';
import { GeoHelper } from '../helpers/geo';
import * as L from 'leaflet';
import * as R from 'leaflet-responsive-popup';
import { ContainerTypeSummary } from '../location-detail/container-type-summary';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { VehicleTypeHelper } from '../helpers/VehicleTypeHelper';
import { CustomerSettings } from '../services/customer-settings-service';

export enum InventryMode {
  Summary,
  Depot,
  NotDepot,
  GroupedSummary
}

export class DropDownListItem {
  id: string;
  name: string;
}

export class ContainerGroupSummary {
  constructor(assetTypeClassification: AssetTypeClassification) {
    this.AssetTypeClassification = assetTypeClassification;
  }

  getTypeCount(filteredAssets : CurrentAssetLocationDto[]) {
    return this.Groups.reduce(function(a, b){
      return a + b.getTypeCount(filteredAssets);
    }, 0);
  }

  getTypeDamagedCount(filteredAssets : CurrentAssetLocationDto[]) {
    return this.Groups.reduce(function(a, b){
      return a + b.getTypeDamagedCount(filteredAssets);
    }, 0);
  }

  getTypeDamagedOnLocationCount(filteredAssets : CurrentAssetLocationDto[]) {
    return this.Groups.reduce(function(a, b){
      return a + b.getTypeDamagedOnLocationCount(filteredAssets);
    }, 0);
  }

  getTypeSurplusCount(filteredAssets : CurrentAssetLocationDto[]) {
    return this.Groups.reduce(function(a, b){
      return a + b.getTypeSurplusCount(filteredAssets);
    }, 0);
  }

  getTypeYardCount(filteredAssets : CurrentAssetLocationDto[]) {
    return this.Groups.reduce(function(a, b){
      return a + b.getTypeYardCount(filteredAssets);
    }, 0);
  }

  AssetTypeClassification: AssetTypeClassification;
  Groups: AssetTypeGroup[];
}

export class AssetTypeGroup {
  constructor(group: AssetTypeGroupDto, selectedAssetLocationId: string, selectedAssetTypeId: string, containerAvailabilityQuantities: ContainerAvailabilityQuantityDto[]) {
    this.Group = group;
    this.SelectedAssetLocationId = selectedAssetLocationId;
    this.SelectedAssetTypeId = selectedAssetTypeId;
    this.ContainerAvailabilityQuantities = containerAvailabilityQuantities;
  }

  Group: AssetTypeGroupDto;
  SelectedAssetLocationId: string;
  SelectedAssetTypeId: string;
  ContainerAvailabilityQuantities: ContainerAvailabilityQuantityDto[];
  Summary: AssetTypeSummary[];

  getTypeCount(assets : CurrentAssetLocationDto[]) {
    return this.Summary.reduce(function(a, b){
      return a + b.getTypeCount(assets);
    }, 0);
  }

  getTypeDamagedCount(assets : CurrentAssetLocationDto[]) {
    return this.Summary.reduce(function(a, b){
      return a + b.getTypeDamagedCount(assets);
    }, 0);
  }

  getTypeDamagedOnLocationCount(assets : CurrentAssetLocationDto[]) {
    return this.Summary.reduce(function(a, b){
      return a + b.getTypeDamagedOnLocationCount(assets);
    }, 0);
  }

  getTypeSurplusCount(assets : CurrentAssetLocationDto[]) {
    let damagedCount = this.getTypeDamagedCount(assets);
    let damagedOnLocationCount = this.getTypeDamagedOnLocationCount(assets);
    let yardCount = this.getTypeYardCount(assets);
    let assetCount = this.getTypeCount(assets);
    let count = assetCount - yardCount - damagedCount + damagedOnLocationCount;
    return count;
  }

  getTypeYardCount(assets : CurrentAssetLocationDto[]) {
    if (!assets?.length) {
      return 0;
    }
   
    if (!this.ContainerAvailabilityQuantities?.length || this.SelectedAssetTypeId) {
      return this.Summary.reduce(function(a, b){
        return a + b.getTypeYardCount(assets);
      }, 0);
    }

    var total = assets.filter(a => a.assetTypeClassification == this.Group.assetTypeClassification && this.Group.assetTypes.some(t => t.name == a.assetType) && a.isYardBin && !(a.damagedDate && !a.responseDate)).length;

    if (!this.SelectedAssetLocationId && this.ContainerAvailabilityQuantities.some(q => !q.locationId)) {
      total += this.ContainerAvailabilityQuantities.filter(q => !q.locationId).map(q => q.yard).reduce((partialSum, a) => partialSum + a, 0);
    }
    else {
      total += this.ContainerAvailabilityQuantities.filter(q => !this.SelectedAssetLocationId || q.locationId == this.SelectedAssetLocationId).map(q => q.yard).reduce((partialSum, a) => partialSum + a, 0);
    }

    return total;
  }
}

export class AssetTypeSummary {
  constructor(name: string, assetTypeClassification: AssetTypeClassification, selectedAssetLocationId: string, containerAvailabilityQuantities: ContainerAvailabilityQuantityDto[]) {
    this.AssetType = name;
    this.AssetTypeClassification = assetTypeClassification;
    this.SelectedAssetLocationId = selectedAssetLocationId;
    this.ContainerAvailabilityQuantities = containerAvailabilityQuantities;
  }

  AssetType: string;
  AssetTypeClassification: AssetTypeClassification;
  SelectedAssetLocationId: string;
  ContainerAvailabilityQuantities: ContainerAvailabilityQuantityDto[];
  
  getTypeCountDisplay(assets: CurrentAssetLocationDto[]): string {
    let count = this.getTypeCount(assets);
    return count == 0 ? "" : count.toString();
  }

  getTypeDamagedCountDisplay(assets: CurrentAssetLocationDto[]): string {
    let count = this.getTypeDamagedCount(assets);
    return  count == 0 ? "" : count.toString();
  }

  getTypeSurplusCountDisplay(assets: CurrentAssetLocationDto[]): string {
    let damagedCount = this.getTypeDamagedCount(assets);
    let damagedOnLocationCount = this.getTypeDamagedOnLocationCount(assets);
    let yardCount = this.getTypeYardCount(assets);
    let assetCount = this.getTypeCount(assets);
    let count = assetCount - yardCount - damagedCount + damagedOnLocationCount;
    return count == 0 ? "" : count.toString();
  }

  getTypeYardCountDisplay(assets: CurrentAssetLocationDto[]): string {
    let types = this.getAssets(assets);
    var count = types.filter(a => a.isYardBin && !(a.damagedDate && !a.responseDate)).length;
    return count == 0 ? "" : count.toString();
  }

  getTypeDamagedCount(assets: CurrentAssetLocationDto[]): number {
    let types = this.getAssets(assets);
    return types.filter(a => a.damagedDate && !a.responseDate).length;
  }

  getTypeDamagedOnLocationCount(assets: CurrentAssetLocationDto[]): number {
    let types = this.getAssets(assets);
    return types.filter(a => a.damagedDate && !a.responseDate && a.locationTypeClassification != LocationTypeClassification.Depot).length;
  }

  getTypeYardCount(assets: CurrentAssetLocationDto[]) {
    if (!assets?.length) {
      return 0;
    }

    var total = assets.filter(a => a.assetTypeClassification == this.AssetTypeClassification && a.assetType == this.AssetType && a.isYardBin && !(a.damagedDate && !a.responseDate)).length;

    if (!this.ContainerAvailabilityQuantities.length) {
      return total;
    }

    if (!this.SelectedAssetLocationId && this.ContainerAvailabilityQuantities.some(q => !q.locationId)) {
      total += this.ContainerAvailabilityQuantities.filter(q => !q.locationId).map(q => q.yard).reduce((partialSum, a) => partialSum + a, 0);
    }
    else {
      total += this.ContainerAvailabilityQuantities.filter(q => !this.SelectedAssetLocationId || q.locationId == this.SelectedAssetLocationId).map(q => q.yard).reduce((partialSum, a) => partialSum + a, 0);
    }

    return total;
  }

  getTypeSurplusCount(assets: CurrentAssetLocationDto[]): number {
    let damagedCount = this.getTypeDamagedCount(assets);
    let damagedOnLocationCount = this.getTypeDamagedOnLocationCount(assets);
    let yardCount = this.getTypeYardCount(assets);
    let assetCount = this.getTypeCount(assets);
    let count = assetCount - yardCount - damagedCount + damagedOnLocationCount;
    return count;
  }

  getTypeCount(assets: CurrentAssetLocationDto[]): number {
    let types = this.getAssets(assets);

    return types.length;
  }

  getAssets(assets: CurrentAssetLocationDto[]): CurrentAssetLocationDto[] {
    return assets.filter(s => s.assetType == this.AssetType);
  }
}

export class AssetSummary {
  constructor(locationId: string, name: string) {
    this.LocationId = locationId;
    this.LocationName = name;
    this.Assets = []
  }
  
  LocationId: string;
  LocationName: string;
  Assets: CurrentAssetLocationDto[]

  getTotal() {
    return this.Assets.length;
  }

  getTypeCountDisplay(type: string): string {
    let count = this.getTypeCount(type);
    return count == 0 ? "" : count.toString();
  }

  getTypeCount(type: string): number {
    let types = this.getAssets(type);
    return types.length;
  }

  getTypeDamagedCount(type: string): number {
    let types = this.getAssets(type);
    return types.filter(t => (t.damagedDate && !t.responseDate)).length;
  }

  getTypeDamagedOnLocationCount(type: string): number {
    let types = this.getAssets(type);
    return types.filter(t => (t.damagedDate && !t.responseDate && t.locationTypeClassification != LocationTypeClassification.Depot)).length;
  }

  getTypeYardCount(type: string): number {
    let types = this.getAssets(type);
    return types.filter(t => t.isYardBin && !(t.damagedDate && !t.responseDate)).length;
  }

  getAssets(type: string): CurrentAssetLocationDto[] {
    return this.Assets.filter(s => s.assetType == type);
  }

  getDamagedCount() {
    return this.Assets.filter(a => (a.damagedDate && !a.responseDate)).length;
  }

  getDamagedOnLocationCount() {
    return this.Assets.filter(a => (a.damagedDate && !a.responseDate && a.locationTypeClassification != LocationTypeClassification.Depot)).length;
  }

  getYardCount() {
    return this.Assets.filter(a => a.isYardBin && !(a.damagedDate && !a.responseDate)).length;
  }

  getSurplusCount() {
    return this.Assets.length - this.getYardCount() - this.getDamagedCount() + this.getDamagedOnLocationCount();
  }
}

export class LocationContainerTypeSummary {
  constructor(name: string) {
    this.Type = name;
    this.CustomerCount = 0;
    this.DamagedCount = 0;
    this.DamagedOnLocationCount = 0;
    this.DepotCount = 0;
    this.OnMoveCount = 0;
    this.OtherCount = 0;
    this.YardCount = 0;
    this.Children = [];
  }
  
  searchCtrl = new UntypedFormControl();
  Type: string;
  CustomerCount: number;  
  DepotCount: number;
  OtherCount: number;
  OnMoveCount: number;
  DamagedCount: number;
  DamagedOnLocationCount: number;
  YardCount: number;
  Children: LocationContainerTypeSummary[];

  getSurplus(): number {
    return this.DepotCount - this.YardCount - this.DamagedCount + this.DamagedOnLocationCount;
  }

  getTotal(): number {
    return this.CustomerCount + this.DepotCount + this.OtherCount + this.OnMoveCount;
  }
}

@Component({
    selector: 'app-inventory-component',
    templateUrl: './inventory.component.html',
    styleUrls: ['./inventory.component.scss'],
    standalone: false
})
export class InventoryComponent extends PagebaseComponent implements OnDestroy, OnInit {
  
  @ViewChild('mapdiv') 
  mapDiv: ElementRef;
  
  // Private variables
  InventryMode = InventryMode;
  currentMode: InventryMode = InventryMode.GroupedSummary;
  locations: CustomerLocationDto[];
  filteredLocations: Observable<CustomerLocationDto[]>;
  assets: CurrentAssetLocationDto[];
  onMove: string[];
  filteredAssets: CurrentAssetLocationDto[] = [];
  roroSummary: LocationContainerTypeSummary[] = [];
  skipsSummary: LocationContainerTypeSummary[] = [];
  containerGroups: AssetTypeGroupDto[] = [];
  totalCustomerCount: number = 0;
  totalDepotCount: number = 0;
  totalOtherCount: number = 0;
  totalOnMoveCount: number = 0;
  totalDamagedCount: number = 0;
  totalDamagedOnLocationCount: number = 0;
  totalSurplus: number = 0;
  totalTotal: number = 0;
  locationTypeList: DropDownListItem[];
  locationsList: DropDownListItem[];
  assetTypeList: DropDownListItem[];
  regionList: DropDownListItem[];  
  depotList: DropDownListItem[];
  containerAvailabilityQuantities: ContainerAvailabilityQuantityDto[];
  depotQuantities: ContainerAvailabilityQuantityDto[];
  locationQuantities: ContainerAvailabilityQuantityDto[];
  selectedAssetLocationTypeId: string;
  selectedAssetLocationId: string;
  selectedAssetTypeId: string;
  selectedRegionId: string;
  selectedDepotId: string;
  selectedVehicleType: string;
  hasMultipleRegions: boolean = false;  
  hasMultipleDepots: boolean = false;
  assetSummaryData: AssetSummary[] = [];
  assetSummaryAssetTypes: AssetTypeSummary[] = [];
  roroContainerGroupSummary : ContainerGroupSummary = new ContainerGroupSummary(AssetTypeClassification.RoRo);
  skipContainerGroupSummary : ContainerGroupSummary = new ContainerGroupSummary(AssetTypeClassification.Skip);
  openLocations: string[] = [];
  openRoroTypes: string[] = [];
  openSkipTypes: string[] = [];
  containerTypeSummaryTotals: ContainerTypeSummary[] = [];
  searchControl: UntypedFormControl = new UntypedFormControl();
  viewModeForm = new UntypedFormGroup({
    viewMode: new UntypedFormControl('both')
  });
  showMode: string = "both";
  locationTypes: LocationTypeDto[];
  locationTypesDisplay: LocationTypeDto[] = [];

  mapZoomLevel: number;

  summaryData = [];
  locationSearchControl: UntypedFormControl = new UntypedFormControl();
  filteredLocationsList: DropDownListItem[];

  private apiCall;

  // Public variables
  loading: boolean = true;

  // Map
  private map;
  private mapLayer = L.tileLayer('http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&key=AIzaSyChxd9LP-pHKK1qe1nPlECcK5iJb-a4hg8', {
      maxZoom: 18,
      minZoom: 3,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
  });
  private drawLayer = L.layerGroup();
  
  // Constuctor
  constructor(
    private _assetLocationService: AssetLocationService,
    private sessionStorageService: SessionStorageService,
    private router: Router,
    pageHelperService: PageHelperService,
    applicationStatusService: ApplicationStatusService,
    private customerSettings: CustomerSettings) {
    super(pageHelperService, applicationStatusService, customerSettings, "Inventory");
  }

  ngOnInit() {
    this.setupAutoComplete();
    this.summaryData = this.getLoadingSummarries();
    this.previewMap()
  }

  ngOnDestroy(): void {
    if (this.map ){
      this.map.eachLayer(function(layer){
          layer.remove();
      });
      this.map.remove();
      this.map = null;
      this.mapDiv.nativeElement.innerHTML = "";
    }
  }

  getLoadingSummarries() : SummaryDataItemModel[] {
    var items = [];
    var text = "";
    text = text.concat("\xa0");
    let blank: SummaryDataItemModel = {
      Title: text,
      Value: text
    };
    items.push(blank);
    items.push(blank);
    items.push(blank);
    items.push(blank);
    return items;
  }

  private previewMap() {
    if (!this.map) {
      this.map = L.map('map', {
        center: [ 54.276050, -2.654715 ],
        zoom: 6,
        scrollWheelZoom: false
      });

      this.mapLayer.addTo(this.map);
      this.drawLayer.addTo(this.map);
      setTimeout(() => {
        this.map.invalidateSize(true);
      }, 500)
    }
  }
  
  reset() {
    this.locations = [];
    this.assets = [];
    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }
  }

  // Get the view data
  getData() {
    this.loading = true;
    this.setIsLoading(this.loading);

    this.apiCall = this._assetLocationService.getCustomer(
        this.pageHelperService.selectedCompanyId
      ).subscribe(data => {
        if (this.sessionStorageService.getItem("inventory-selectedCustomerId") != this.pageHelperService.selectedCompanyId) {
          this.sessionStorageService.removeItem("inventory-selectedCustomerId");
          this.sessionStorageService.removeItem("inventory-selectedRegionId");
          this.sessionStorageService.removeItem("inventory-selectedDepotId");
          this.selectedRegionId = null;
          this.selectedDepotId = null;
        }
        this.locations = data.locations;
        this.assets = data.assets;
        this.onMove = data.assetsOnTheMove;
        this.roroSummary = this.calculateSummary();
        this.skipsSummary = this.calculateSummary();
        this.summaryData = this.getSummaryData();
        this.loading = false;
        this.locationTypes = data.locationTypes;
        this.containerGroups = data.containerGroups;
        this.containerAvailabilityQuantities = data.containerAvailabilityQuantities;
        this.initMap();
        this.showAssets(true);
        this.loadListData(data.assets, data.assets);
        this.setIsLoading(this.loading);
      }, error => {
        debugger;
      });
  }

  viewModeChange(e) {
    this.showMode = e.target.value;
    const map = this.map;
    setTimeout(function(){ map.invalidateSize() }, 400);
  }

  setupAutoComplete() {
    this.filteredLocations = this.searchControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterLocations(value)),
    );
  }

  toggleRow(locationId) {
    if (this.openLocations.includes(locationId)) {
      const index = this.openLocations.indexOf(locationId);
      if (index > -1) {
        this.openLocations.splice(index, 1);
      }
    } else {
      this.openLocations.push(locationId);
    }
  }

  toggleRoroSummaryRow(type) {
    if (this.openRoroTypes.includes(type)) {
      const index = this.openRoroTypes.indexOf(type);
      if (index > -1) {
        this.openRoroTypes.splice(index, 1);
      }
    } else {
      this.openRoroTypes.push(type);
    }
  }

  toggleSkipSummaryRow(type) {
    if (this.openSkipTypes.includes(type)) {
      const index = this.openSkipTypes.indexOf(type);
      if (index > -1) {
        this.openSkipTypes.splice(index, 1);
      }
    } else {
      this.openSkipTypes.push(type);
    }
  }

  private calculateSummary(): LocationContainerTypeSummary[] {
    this.filteredAssets = [];

    let selectedVehicleType = this.sessionStorageService.getItem("dashboard-active-selectedVehicleType");
    if(selectedVehicleType) {
      selectedVehicleType = JSON.parse(this.sessionStorageService.getItem("dashboard-active-selectedVehicleType"));
      this.selectedVehicleType = selectedVehicleType;
    }
    let selectedRegionId = this.sessionStorageService.getItem("inventory-selectedRegionId");
    if (selectedRegionId) {
      this.selectedRegionId = selectedRegionId;
    }
    let selectedDepotId = this.sessionStorageService.getItem("inventory-selectedDepotId");
    if(selectedDepotId) {
      this.selectedDepotId = selectedDepotId;
    }

    let summaries: LocationContainerTypeSummary[] = [];
    this.assets.forEach(asset => {

      let location = this.locations.find(l => l.id == asset.locationId);

      let includeAsset = true;

      if (!location) {
        includeAsset = false;
      }
      else {
        if (selectedVehicleType && asset.assetTypeClassification != VehicleTypeHelper.ConvertVehicleTypeNameToAssetTypeClassification(selectedVehicleType)) {
          includeAsset = false;
        }
     
        if (selectedRegionId && location.customerRegionId != selectedRegionId) {
          includeAsset = false;
        }
        
        if (selectedDepotId && location.depotId != selectedDepotId) {
          includeAsset = false;
        }
      }

      if(includeAsset) {
        this.filteredAssets.push(asset);
        let summary = summaries.find(s => s.Type == asset.assetType)
        if (!summary) {
          summary = new LocationContainerTypeSummary(asset.assetType);
          summaries.push(summary);
        }

        let location = this.getLocation(asset.locationId);
        if (location) {

          var onMove = false;
          if (this.onMove.find(o => o == asset.id)) {
            summary.OnMoveCount++;
          } else {
            switch(location.locationTypeClassification) {
              case LocationTypeClassification.Customer:
                summary.CustomerCount++;
                break;
              case LocationTypeClassification.Depot:
                summary.DepotCount++;
                break;
              default:
                summary.OtherCount++;
            }
          }

          if ((asset.damagedDate && !asset.responseDate)) {
            summary.DamagedCount++;

            if (asset.locationTypeClassification != LocationTypeClassification.Depot) {
              summary.DamagedOnLocationCount++;
            }
          }
          else if (asset.isYardBin) {
            summary.YardCount++;
          }
        }
      }
    });

    // Set totals
    this.totalCustomerCount = 0;
    this.totalDepotCount = 0;
    this.totalOtherCount = 0;
    this.totalOnMoveCount = 0;
    this.totalDamagedCount = 0;
    this.totalDamagedOnLocationCount = 0;
    this.totalSurplus = 0;
    this.totalTotal = 0;
    summaries.forEach(summary => {
      this.totalCustomerCount += summary.CustomerCount;
      this.totalDepotCount += summary.DepotCount;
      this.totalOtherCount += summary.OtherCount;
      this.totalOnMoveCount += summary.OnMoveCount;
      this.totalDamagedCount += summary.DamagedCount;
      this.totalDamagedOnLocationCount += summary.DamagedOnLocationCount;
      this.totalSurplus += summary.getSurplus();
      this.totalTotal += summary.getTotal();
    });

    /*
    let damagedCount = this.totalDamagedCount;
    let damagedOnLocationCount = this.totalDamagedOnLocationCount;
    let yardCount = this.getYardTotal();
    let assetCount = this.totalDepotCount;
    let count = assetCount - yardCount - damagedCount + damagedOnLocationCount;
    this.totalSurplus = count;
    */

    // sort 
    summaries = summaries.sort(function(a, b){
      if(a.getTotal() < b.getTotal()) { return 1; }
      if(a.getTotal() > b.getTotal()) { return -1; }
      return 0;
    })

    return summaries;
  }

  getSummaryData(): SummaryDataItemModel[] {
    let items = [];
    let total: SummaryDataItemModel = {
      Title: "Total Inventory",
      Value: this.totalTotal.toString()
    };
    items.push(total);

    let available: SummaryDataItemModel = {
      Title: "Available",
      Value: this.totalSurplus.toString()
    };
    items.push(available);

    let customer: SummaryDataItemModel = {
      Title: "At Customer",
      Value: this.totalCustomerCount.toString()
    };
    items.push(customer);

    let damaged: SummaryDataItemModel = {
      Title: "Damaged",
      Value: this.totalDamagedCount.toString()
    };
    items.push(damaged);
    return items;
  }

  private initMap(): void {
    let allLatLons = [];
    this.locations.forEach(location => {
      allLatLons.push({ lat: location.latitude, lon: location.longitude })
    });
    
    let centre = GeoHelper.GetCenterFromPositions(allLatLons);
    
    if (!this.map) {
      let zoom = GeoHelper.CalculateZoom(allLatLons, this.mapDiv.nativeElement.offsetHeight, this.mapDiv.nativeElement.offsetWidth);
      this.map = L.map('map', {
        center: [ centre.lat, centre.lon ],
        zoom: zoom,
        scrollWheelZoom: false
      });

      this.mapLayer.addTo(this.map);
      this.drawLayer.addTo(this.map);
      setTimeout(() => {
        this.map.invalidateSize(true);
      }, 1)
    } else {
      this.map.flyTo(new L.LatLng(centre.lat, centre.lon), 15, {
        animate: true,
        duration: 1.5
      });
    }
    let ctrl = this;
    this.map.on('zoomend', function() {
      if (ctrl.mapZoomLevel != ctrl.map.getZoom()) {
        ctrl.mapZoomLevel = ctrl.map.getZoom();
        
        ctrl.showAssets(false);
      }
    });

    this.drawLocations(null, true)
  }

  drawLocations(assets: CurrentAssetLocationDto[], recentreMap: boolean) {
    if (assets == null) {
      return;
    }

    // Clear  and re add draw layer
    let allLatLons = [];
    this.map.removeLayer(this.drawLayer);
    this.drawLayer = L.layerGroup();
    this.drawLayer.addTo(this.map);
    const locationTypesDisplay = []
    
    this.locations.forEach(location => {

      if (assets == null || assets.some(a => a.locationId == location.id)) {
        allLatLons.push({ lat: location.latitude, lon: location.longitude })
      }
    });

    if (recentreMap && this.showMode != "table" && allLatLons.length > 0) {
      let centre = GeoHelper.GetCenterFromPositions(allLatLons);
      let zoom = GeoHelper.CalculateZoom(allLatLons, this.mapDiv.nativeElement.offsetHeight, this.mapDiv.nativeElement.offsetWidth);
      zoom = allLatLons.length == 1 ? 12 : zoom;
      this.mapZoomLevel = zoom;
      this.map.flyTo(new L.LatLng(centre.lat, centre.lon), zoom, {
        animate: true,
        duration: 1.5
      });
    }

    this.locations.forEach(location => {
      let locationBins = this.getBinsAtLocation(location.id);
      if (assets == null || assets.some(a => a.locationId == location.id)) {
        this.addLocationPin(location.id, location.locationType, location.displayName, location.latitude, location.longitude, locationBins);

        if (!locationTypesDisplay.find(l => l.name == location.locationType)) {
          var locationTypeNameDisplay = this.locationTypes.find(lt => lt.name == location.locationType);
          locationTypesDisplay.push(locationTypeNameDisplay)
        }
      }
    });   
    
    this.locationTypesDisplay = locationTypesDisplay;
  }

  addLocationPin(locationId, locationTypeName, locationName, locationLatitude, locationLongitude, locationBins: CurrentAssetLocationDto[]) {
    let pinColour = locationTypeName == null ? "vehicle" : LocationClassNameHelper.getLocationColour(locationTypeName);
    
    let radius = this.getLocationRadius(this.mapZoomLevel);
    
    //Calculate dwell
    const sum = locationBins.map(b => b.daysAtLocation).reduce((a, b) => a + b, 0);
    const avg = Math.round((sum / locationBins.length) || 0);

    let popupContent = '<p>' + locationName + '</p>';
    // popupContent += `<div class="row">`
    // popupContent += `<div class="col-md-8">`
    // popupContent += locationBins.map(b => b.id).join(', ');
    // popupContent += `</div>`
    // popupContent += `<div class="col-md-2 tooltip-info-box"><div class="value">${ avg }</div><div class="label">Dwell Time</div></div>`
    // popupContent += `</div>`
    const popup = R.responsivePopup({ hasTip: false, autoPan: false, offset: [20, 20]}).setContent(popupContent);
    let marker = L.circle([locationLatitude, locationLongitude], { 
      radius: radius,
      color: "black",
      weight: 1,
      fillColor: pinColour,
      fillOpacity: 1,
     }).addTo(this.drawLayer);
    let showTip = function() {
      this.unbindTooltip();
      if(!this.isPopupOpen()) this.bindPopup(popup, { minWidth: 280}).openPopup();
    }
    
    let hideTip = function() {
      this.unbindTooltip();
      if(this.isPopupOpen()) this.closePopup();
    }
    
    let router = this.router;
    let commands = this.getLocationNavigationCommands(locationId);
    let goToRoute = function() {
      router.navigate(commands);
    }

    marker.on('mouseover', showTip);
    marker.on('mouseout', hideTip);
    marker.on('click', goToRoute);
  }

  getLocationRadius(zoom: number) {
    switch(zoom) {
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
        return 2000;
      case 7:
        return 1500;
      case 8:
        return 1250;
      case 9:
        return 900;
      case 10:
        return 540;
      case 11: 
        return 324;
      case 12:
        return 194;
      case 13:
        return 116;
      case 14:
        return 70;
      case 15:
        return 42;
      case 16:
        return 25;
      case 17:
        return 15;
      default:
        return 10;
    }
  }

  getBinsAtLocation(locationId: string) : CurrentAssetLocationDto[]{
    return this.assets.filter(a => a.locationId == locationId);
  }

  getLocation(locationId: string) : CustomerLocationDto{
    return this.locations.find(a => a.id == locationId);
  }

  showAssets(recentreMap: boolean) {
    this.filteredAssets = [];
    let selectedAssetLocationTypeId = this.sessionStorageService.getItem("inventory-selectedAssetLocationTypeId");
    if(selectedAssetLocationTypeId) {
      this.selectedAssetLocationTypeId = selectedAssetLocationTypeId;
    }
    let selectedAssetLocationId = this.sessionStorageService.getItem("inventory-selectedAssetLocationId");
    if(selectedAssetLocationId) {
      this.selectedAssetLocationId = selectedAssetLocationId;
    }
    let selectedAssetTypeId = this.sessionStorageService.getItem("inventory-selectedAssetTypeId");
    if(selectedAssetTypeId) {
      this.selectedAssetTypeId = selectedAssetTypeId;
    }
    let selectedRegionId = this.sessionStorageService.getItem("inventory-selectedRegionId");
    if(selectedRegionId) {
      this.selectedRegionId = selectedRegionId;
    }    
    let selectedDepotId = this.sessionStorageService.getItem("inventory-selectedDepotId");
    if(selectedDepotId) {
      this.selectedDepotId = selectedDepotId;
    }
    let selectedVehicleType = this.sessionStorageService.getItem("dashboard-active-selectedVehicleType");
    if(selectedVehicleType) {
      selectedVehicleType = JSON.parse(this.sessionStorageService.getItem("dashboard-active-selectedVehicleType"));
      this.selectedVehicleType = selectedVehicleType;
    }

    let mode: InventryMode = InventryMode.GroupedSummary;
    if(selectedAssetLocationTypeId || selectedAssetLocationId || selectedAssetTypeId) {
      mode = selectedAssetLocationTypeId == LocationTypeNames.Depot ? InventryMode.Depot : InventryMode.NotDepot;
    }
    
    let assetSummaries: AssetSummary[] = [];
    let assetTypeSummary: AssetTypeSummary[] = [];

    switch(mode) {
      case InventryMode.NotDepot:
        this.assets.forEach(asset => {
          let includeAsset = true;
          let location = this.locations.find(l => l.id == asset.locationId);
  
          if (!location) {
            includeAsset = false;
          }
          else {
            if (selectedRegionId && location.customerRegionId != selectedRegionId) {
              includeAsset = false;
            }
    
            if (selectedDepotId && location.depotId != selectedDepotId) {
              includeAsset = false;
            }
    
            if (selectedAssetLocationTypeId && location.locationType != selectedAssetLocationTypeId) {
              includeAsset = false;
            }
    
            if (selectedAssetLocationId && asset.locationId != selectedAssetLocationId) {
              includeAsset = false;
            }
    
            if (selectedAssetTypeId && asset.assetType != selectedAssetTypeId) {
              includeAsset = false;
            }
    
            if (selectedVehicleType && asset.assetTypeClassification != VehicleTypeHelper.ConvertVehicleTypeNameToAssetTypeClassification(selectedVehicleType)) {
              includeAsset = false;
            }
    
            if (this.onMove.find(o => o == asset.id)) {
              includeAsset = false;
            }
          }
  
          if (includeAsset) {
            this.filteredAssets.push(asset)
            let assetSummary = assetSummaries.find(a => a.LocationId == asset.locationId);
            
            if (!assetSummary) {
              assetSummary = new AssetSummary(asset.locationId, location.displayName);
              assetSummaries.push(assetSummary)
            }
  
            // Check if the type exists in asset type master list.
            if(!assetTypeSummary.find(a => a.AssetType == asset.assetType && a.AssetTypeClassification == asset.assetTypeClassification)) {
              assetTypeSummary.push(new AssetTypeSummary(asset.assetType, asset.assetTypeClassification, this.selectedAssetLocationId, this.containerAvailabilityQuantities.filter(q => q.assetTypeClassification == asset.assetTypeClassification && q.assetTypeName == asset.assetType && q.yard)));
            }
  
            assetSummary.Assets.push(asset);
          }
        });

        this.assetSummaryAssetTypes = assetTypeSummary;
        this.assetSummaryData = assetSummaries;
        this.setAssetTypeGroups();
        
        this.assetSummaryData = assetSummaries.sort(function(a, b){
          if(a.LocationName < b.LocationName) { return -1; }
          if(a.LocationName > b.LocationName) { return 1; }
          return 0;
        })
  
        this.containerTypeSummaryTotals = [];

        this.drawLocations(this.filteredAssets == null ? this.assets : this.filteredAssets, recentreMap);
        break;
      case InventryMode.Depot:
        this.assets.forEach(asset => {
          let includeAsset = true;
          let location = this.locations.find(l => l.id == asset.locationId);
  
          if (!location) {
            includeAsset = false;
          }
          else {
            if (selectedRegionId && location.customerRegionId != selectedRegionId) {
              includeAsset = false;
            }
    
            if (selectedDepotId && location.depotId != selectedDepotId) {
              includeAsset = false;
            }
          
            if (selectedAssetLocationTypeId && location.locationType != selectedAssetLocationTypeId) {
              includeAsset = false;
            }
    
            if (selectedAssetLocationId && asset.locationId != selectedAssetLocationId) {
              includeAsset = false;
            }
    
            if (selectedAssetTypeId && asset.assetType != selectedAssetTypeId) {
              includeAsset = false;
            }
    
            if (selectedVehicleType && asset.assetTypeClassification != VehicleTypeHelper.ConvertVehicleTypeNameToAssetTypeClassification(selectedVehicleType)) {
              includeAsset = false;
            }

            if (this.onMove.find(o => o == asset.id)) {
              includeAsset = false;
            }
          }

          if (includeAsset) {
            this.filteredAssets.push(asset)
            let assetSummary = assetSummaries.find(a => a.LocationId == asset.locationId);
            
            if (!assetSummary) {
              assetSummary = new AssetSummary(asset.locationId, location.displayName);
              assetSummaries.push(assetSummary)
            }
              
            // Check if the type exists in asset type master list.
            if(!assetTypeSummary.find(a => a.AssetType == asset.assetType && a.AssetTypeClassification == asset.assetTypeClassification)) {
              assetTypeSummary.push(new AssetTypeSummary(asset.assetType, asset.assetTypeClassification, this.selectedAssetLocationId, this.containerAvailabilityQuantities.filter(q => q.assetTypeClassification == asset.assetTypeClassification && q.assetTypeName == asset.assetType && q.yard)));
            }
  
            assetSummary.Assets.push(asset);
          }
        });

        this.assetSummaryAssetTypes = assetTypeSummary;
        this.assetSummaryData = assetSummaries;
        this.setAssetTypeGroups();
  
        this.containerTypeSummaryTotals = [];
        this.drawLocations(this.filteredAssets == null ? this.assets : this.filteredAssets, recentreMap);
        break;
        case InventryMode.GroupedSummary:
          this.assetSummaryAssetTypes = [];
          this.assetSummaryData = [];
          this.containerTypeSummaryTotals = [];
          this.filteredAssets = [];
          this.roroSummary = this.calculateGroupedSummary(AssetTypeClassification.RoRo); 
          this.skipsSummary = this.calculateGroupedSummary(AssetTypeClassification.Skip); 
        
          // Set totals
          this.totalCustomerCount = 0;
          this.totalDepotCount = 0;
          this.totalOtherCount = 0;
          this.totalOnMoveCount = 0;
          this.totalDamagedCount = 0;
          this.totalDamagedOnLocationCount = 0;
          this.totalSurplus = 0;
          this.totalTotal = 0;
          this.roroSummary.forEach(summary => {
            this.totalCustomerCount += summary.CustomerCount;
            this.totalDepotCount += summary.DepotCount;
            this.totalOtherCount += summary.OtherCount;
            this.totalOnMoveCount += summary.OnMoveCount;
            this.totalDamagedCount += summary.DamagedCount;
            this.totalDamagedOnLocationCount += summary.DamagedOnLocationCount;
            this.totalSurplus += summary.getSurplus();
            this.totalTotal += summary.getTotal();

            summary.Children.sort(function(a, b){
              if(a.getTotal() > b.getTotal()) { return -1; }
              if(a.getTotal() < b.getTotal()) { return 1; }
              return 0;
            });
          });
          this.skipsSummary.forEach(summary => {
            this.totalCustomerCount += summary.CustomerCount;
            this.totalDepotCount += summary.DepotCount;
            this.totalOtherCount += summary.OtherCount;
            this.totalOnMoveCount += summary.OnMoveCount;
            this.totalDamagedCount += summary.DamagedCount;
            this.totalDamagedOnLocationCount += summary.DamagedOnLocationCount;
            this.totalSurplus += summary.getSurplus();
            this.totalTotal += summary.getTotal();

            summary.Children.sort(function(a, b){
              if(a.getTotal() > b.getTotal()) { return -1; }
              if(a.getTotal() < b.getTotal()) { return 1; }
              return 0;
            });
          });

          /*
          let damagedCount = this.totalDamagedCount;
          let damagedOnLocationCount = this.totalDamagedOnLocationCount;
          let yardCount = this.getYardTotal();
          let assetCount = this.totalDepotCount;
          let count = assetCount - yardCount - damagedCount + damagedOnLocationCount;
          this.totalSurplus = count;        
          */

          this.drawLocations(this.filteredAssets, recentreMap);
          break;
        default:
          this.assetSummaryAssetTypes = [];
          this.assetSummaryData = [];
          this.containerTypeSummaryTotals = [];
          this.roroSummary = this.calculateSummary(); 
          this.skipsSummary = this.calculateSummary(); 
          this.drawLocations(this.filteredAssets, recentreMap);
    }

    this.currentMode = mode;
    this.summaryData = this.getSummaryData();
  }
  
  setAssetTypeGroups(){
    let roroGroups : AssetTypeGroup[] = [];
    let skipGroups : AssetTypeGroup[] = [];

    if (this.containerGroups) {
      this.containerGroups
        .filter(g => g.assetTypeClassification == this.roroContainerGroupSummary.AssetTypeClassification)
        .forEach(group => {
          let assetTypeGroup = new AssetTypeGroup(group, this.selectedAssetLocationId, this.selectedAssetTypeId, this.containerAvailabilityQuantities.filter(q => q.assetTypeClassification == group.assetTypeClassification && q.assetTypeGroupName == group.name && q.yard));
          let filteredAssets = this.filteredAssets;

          if (group.assetTypes) {
            let assetTypes = group.assetTypes.map(t => new AssetTypeSummary(t.name, t.assetTypeClassification, this.selectedAssetLocationId, this.containerAvailabilityQuantities.filter(q => q.assetTypeClassification == t.assetTypeClassification && q.assetTypeName == t.name && q.yard))).filter(t => t.getTypeCount(filteredAssets) > 0);

            assetTypes.sort(function(a, b){
              if(a.getTypeCount(filteredAssets) > b.getTypeCount(filteredAssets)) { return -1; }
              if(a.getTypeCount(filteredAssets) < b.getTypeCount(filteredAssets)) { return 1; }
              return 0;
            });

            assetTypeGroup.Summary = assetTypes;
          }

          if (assetTypeGroup.getTypeCount(filteredAssets) > 0) {
            roroGroups.push(assetTypeGroup)
          }
        });

      this.containerGroups
        .filter(g => g.assetTypeClassification == this.skipContainerGroupSummary.AssetTypeClassification)
        .forEach(group => {
          let assetTypeGroup = new AssetTypeGroup(group, this.selectedAssetLocationId, this.selectedAssetTypeId, this.containerAvailabilityQuantities.filter(q => q.assetTypeClassification == group.assetTypeClassification && q.assetTypeGroupName == group.name && q.yard));
          let filteredAssets = this.filteredAssets;

          if (group.assetTypes) {
            let assetTypes = group.assetTypes.map(t => new AssetTypeSummary(t.name, t.assetTypeClassification, this.selectedAssetLocationId, this.containerAvailabilityQuantities.filter(q => q.assetTypeClassification == t.assetTypeClassification && q.assetTypeName == t.name && q.yard))).filter(t => t.getTypeCount(filteredAssets) > 0);

            assetTypes.sort(function(a, b){
              if(a.getTypeCount(filteredAssets) > b.getTypeCount(filteredAssets)) { return -1; }
              if(a.getTypeCount(filteredAssets) < b.getTypeCount(filteredAssets)) { return 1; }
              return 0;
            });

            assetTypeGroup.Summary = assetTypes;
          }

          if (assetTypeGroup.getTypeCount(filteredAssets) > 0) {
            skipGroups.push(assetTypeGroup)
          }
        });
    }

    let filteredAssets = this.filteredAssets;

    roroGroups.sort(function(a, b){
      if(a.getTypeCount(filteredAssets) > b.getTypeCount(filteredAssets)) { return -1; }
      if(a.getTypeCount(filteredAssets) < b.getTypeCount(filteredAssets)) { return 1; }
      return 0;
    })
    skipGroups.sort(function(a, b){
      if(a.getTypeCount(filteredAssets) > b.getTypeCount(filteredAssets)) { return -1; }
      if(a.getTypeCount(filteredAssets) < b.getTypeCount(filteredAssets)) { return 1; }
      return 0;
    })

    this.roroContainerGroupSummary.Groups = roroGroups;
    this.skipContainerGroupSummary.Groups = skipGroups;
  }

  private calculateGroupedSummary(assetTypeClassification: AssetTypeClassification): LocationContainerTypeSummary[] {
    let selectedVehicleType = this.sessionStorageService.getItem("dashboard-active-selectedVehicleType");
    if(selectedVehicleType) {
      selectedVehicleType = JSON.parse(this.sessionStorageService.getItem("dashboard-active-selectedVehicleType"));
      this.selectedVehicleType = selectedVehicleType;
    }
    let selectedRegionId = this.sessionStorageService.getItem("inventory-selectedRegionId");
    if (selectedRegionId) {
      this.selectedRegionId = selectedRegionId;
    }
    let selectedDepotId = this.sessionStorageService.getItem("inventory-selectedDepotId");
    if(selectedDepotId) {
      this.selectedDepotId = selectedDepotId;
    }

    let summaries: LocationContainerTypeSummary[] = [];
    this.assets.forEach(asset => {

      let location = this.locations.find(l => l.id == asset.locationId);
      let includeAsset = true;

      if (!location) {
        includeAsset = false;
      }
      else {
        if(asset.assetTypeClassification != assetTypeClassification) {
          includeAsset = false;
        }

        if (selectedVehicleType && asset.assetTypeClassification != VehicleTypeHelper.ConvertVehicleTypeNameToAssetTypeClassification(selectedVehicleType)) {
          includeAsset = false;
        }
    
        if (selectedRegionId && location.customerRegionId != selectedRegionId) {
          includeAsset = false;
        }
        
        if (selectedDepotId && location.depotId != selectedDepotId) {
          includeAsset = false;
        }
      }
      
      if(includeAsset) {
        this.filteredAssets.push(asset);
        var group = this.findAssetTypeGroupWithId(asset.assetType);
        if (!group) {
          group = {
            id: 0,
            name: "Unknown",
            assetTypes: [],
            assetTypeClassification: null,
            category: null,
            size: null
          }
        }
        
        let summary = summaries.find(s => s.Type == group.name)
        if (!summary) {
          summary = new LocationContainerTypeSummary(group.name);
          summaries.push(summary);
        }
        let childSummary = summary.Children.find(s => s.Type == asset.assetType);
        if (!childSummary) {
          childSummary = new LocationContainerTypeSummary(asset.assetType);
          summary.Children.push(childSummary);
        }
        
        let location = this.getLocation(asset.locationId);
        if (location) {

          var onMove = false;
          if (this.onMove.find(o => o == asset.id)) {
            summary.OnMoveCount++;
            childSummary.OnMoveCount++;
          } else {
            switch(location.locationTypeClassification) {
              case LocationTypeClassification.Customer:
                summary.CustomerCount++;
                childSummary.CustomerCount++;
                break;
              case LocationTypeClassification.Depot:
                summary.DepotCount++;
                childSummary.DepotCount++;
                break;
              default:
                summary.OtherCount++;
                childSummary.OtherCount++;
            }
          }

          if ((asset.damagedDate && !asset.responseDate)) {
            summary.DamagedCount++;
            childSummary.DamagedCount++;
        
            if (asset.locationTypeClassification != LocationTypeClassification.Depot) {
              summary.DamagedOnLocationCount++;
              childSummary.DamagedOnLocationCount++;
            }
          }
          else if (asset.isYardBin) {
            summary.YardCount++;
            childSummary.YardCount++;
          }
        }
      }
    })

    // sort 
    summaries = summaries.sort(function(a, b){
      if(a.getTotal() < b.getTotal()) { return 1; }
      if(a.getTotal() > b.getTotal()) { return -1; }
      return 0;
    })

    return summaries;
  }

  findAssetTypeGroupWithId(assetTypeName: string): AssetTypeGroupDto | undefined {
    return this.containerGroups.find(group => {
        if (group.assetTypes) {
            return group.assetTypes.some(assetType => assetType.name === assetTypeName);
        }

        return false;
    });
}

  resetOtherFilters() {
    this.selectedAssetLocationTypeId = "0: undefined";
    this.selectedAssetLocationId = "0: undefined";
    this.selectedAssetTypeId = "0: undefined";
    this.sessionStorageService.removeItem("inventory-selectedAssetLocationTypeId");
    this.sessionStorageService.removeItem("inventory-selectedAssetLocationId");
    this.sessionStorageService.removeItem("inventory-selectedAssetTypeId");
  }

  locationTypeChange(newValue) {
    if (newValue) {
      this.sessionStorageService.setItem("inventory-selectedAssetLocationTypeId", newValue);
    } else {
      this.sessionStorageService.removeItem("inventory-selectedAssetLocationTypeId")
    }
    
    this.showAssets(true);
  }

  locationChange(newValue) {
    if (newValue) {
      this.sessionStorageService.setItem("inventory-selectedAssetLocationId", newValue);
    } else {
      this.locationSearchControl.setValue("");
      this.sessionStorageService.removeItem("inventory-selectedAssetLocationId")
    }

    this.showAssets(true);
  }

  assetTypeChange(newValue) {
    if (newValue) {
      this.sessionStorageService.setItem("inventory-selectedAssetTypeId", newValue);
    } else {
      this.sessionStorageService.removeItem("inventory-selectedAssetTypeId")
    }

    this.showAssets(true);
  }

  regionChange(newValue) {
    this.sessionStorageService.setItem("inventory-selectedCustomerId", this.pageHelperService.selectedCompanyId);

    if (newValue) {
      this.sessionStorageService.setItem("inventory-selectedRegionId", newValue);
    } else {
      this.sessionStorageService.removeItem("inventory-selectedRegionId")
    }

    this.sessionStorageService.removeItem("inventory-selectedDepotId")
    this.selectedDepotId = null;
    this.resetOtherFilters()
    this.showAssets(true);
    this.loadListData(this.assets, this.filteredAssets);
  }

  depotChange(newValue) {
    if(newValue == "0: undefined"){
      newValue = undefined;
    }
    this.sessionStorageService.setItem("inventory-selectedCustomerId", this.pageHelperService.selectedCompanyId);

    if (newValue) {
      this.sessionStorageService.setItem("inventory-selectedDepotId", newValue);
    } else {
      this.sessionStorageService.removeItem("inventory-selectedDepotId")
    }

    this.resetOtherFilters()
    this.showAssets(true);
    // this.loadListData(this.assets, this.filteredAssets);
  }

  getLocationsList() : DropDownListItem[] {
    let selectedDepotId = this.sessionStorageService.getItem("inventory-selectedDepotId");
    let selectedAssetLocationTypeId = this.sessionStorageService.getItem("inventory-selectedAssetLocationTypeId");
    let locationsList = this.locationsList;

    if (selectedDepotId && this.locations && this.locations.length > 0) {
      let filteredLocations = this.locations.filter(l => l.depotId == selectedDepotId).map(l => l.id)

      locationsList = locationsList.filter(l => filteredLocations.includes(l.id));
    }

    if (selectedAssetLocationTypeId && this.locations && this.locations.length > 0) {
      let filteredLocations = this.locations.filter(l => l.locationType == selectedAssetLocationTypeId).map(l => l.id)
      
      locationsList = locationsList.filter(l => filteredLocations.includes(l.id));
    }

    return locationsList;
  }
  
  onLocationSearch(value: string) { 
    if (value == "" || !value) {
      this.filteredLocationsList = this.locationsList;
    }

    let filter = value.toLowerCase();
    this.filteredLocationsList = this.locationsList.filter(l => l.name.toLowerCase().includes(filter));
  }

  selectedVehicleTypeValueChange(newValue) {
    if (newValue) {
      this.sessionStorageService.setItem("dashboard-active-selectedVehicleType", JSON.stringify(newValue));
    } else {
      this.sessionStorageService.removeItem("dashboard-active-selectedVehicleType")
    }

    this.selectedVehicleType = newValue;
    this.showAssets(true);    
  }


  loadListData(assets: CurrentAssetLocationDto[], filteredAssets: CurrentAssetLocationDto[]) {
    // Get list data from the provided data
    const locationTypeList: DropDownListItem[] = [];
    const locationsList: DropDownListItem[] = [];
    const assetTypeList: DropDownListItem[] = [];
    const regionList: DropDownListItem[] = [];
    const depotList: DropDownListItem[] = [];
    
    assets.forEach(asset => {
      let location = this.locations.find(l => l.id == asset.locationId);

      if (!location) {
        return;
      }

      if (location.customerRegionId && !regionList.find(l => l.id == location.customerRegionId)) {
        let region = new DropDownListItem()
        region.id = location.customerRegionId;
        region.name = location.customerRegionName;
        regionList.push(region);
      }

      if ((!this.selectedRegionId || location.customerRegionId == this.selectedRegionId) && !depotList.find(l => l.id == location.depotId)) {
        let depot = new DropDownListItem()
        depot.id = location.depotId;
        depot.name = location.depotName;
        depotList.push(depot);
      }
    });  

    filteredAssets.forEach(asset => {
      let location = this.locations.find(l => l.id == asset.locationId);

      if (!location) {     
        return;
      }
      
      if (this.selectedRegionId && location.customerRegionId == this.selectedRegionId && !depotList.find(l => l.id == location.depotId)) {
        let depot = new DropDownListItem()
        depot.id = location.depotId;
        depot.name = location.depotName;
        depotList.push(depot);
      }

      if (!locationTypeList.find(l => l.id == location.locationType)) {
        let locationType = new DropDownListItem()
        locationType.id = location.locationType;
        locationType.name = location.locationType;
        locationTypeList.push(locationType)
      }
      
      if (!locationsList.find(l => l.id == location.id)) {
        let locationItem = new DropDownListItem()
        locationItem.id = location.id;
        locationItem.name = location.displayName;
        locationsList.push(locationItem)
      }

      if (!assetTypeList.find(l => l.id == asset.assetType)) {
        let assetItem = new DropDownListItem()
        assetItem.id = asset.assetType;
        assetItem.name = asset.assetType;
        assetTypeList.push(assetItem)
      }
    })

    this.locationTypeList = locationTypeList.sort(function(a, b){
      if(a.name < b.name) { return -1; }
      if(a.name > b.name) { return 1; }
      return 0;
    })
      
    this.locationsList = locationsList.sort(function(a, b){
      if(a.name < b.name) { return -1; }
      if(a.name > b.name) { return 1; }
      return 0;
    })
    this.filteredLocationsList = locationsList;
      
    this.assetTypeList = assetTypeList.sort(function(a, b){
      if(a.name < b.name) { return -1; }
      if(a.name > b.name) { return 1; }
      return 0;
    })

    this.depotList = depotList.sort(function(a, b){
      if(a.name < b.name) { return -1; }
      if(a.name > b.name) { return 1; }
      return 0;
    })

    this.regionList = regionList.sort(function(a, b){
      if(a.name < b.name) { return -1; }
      if(a.name > b.name) { return 1; }
      return 0;
    })

    this.hasMultipleRegions = regionList.length > 1;
    this.hasMultipleDepots = depotList.length > 1;
  }

  getAssetTypeTotal(type: string): number {
    let total = 0;
    this.assetSummaryData.forEach(asset => {
        total += asset.getTypeCount(type);
    });
    return total;
  }

  getTotal() {
    let total = 0;
    this.assetSummaryData.forEach(asset => {
        total += asset.Assets.length;
    });

    return total;
  }

  getYardTotal() {
    if (!this.filteredAssets) {
      return 0;
    }

    var total = this.filteredAssets.filter(a => a.isYardBin && !(a.damagedDate && !a.responseDate)).length;

    if (!this.containerAvailabilityQuantities?.length || !this.containerAvailabilityQuantities.some(q => q.yard)) {
      return total;
    }

    var quantities = this.containerAvailabilityQuantities;

    quantities = this.selectedDepotId && this.selectedDepotId != "0: undefined" ? quantities.filter(q => q.depotId == this.selectedDepotId) : quantities;
    quantities = this.selectedAssetTypeId && this.selectedAssetTypeId != "0: undefined" ? quantities.filter(q => q.assetTypeName == this.selectedAssetTypeId) : quantities;

    if ((!this.selectedAssetLocationId || this.selectedAssetLocationId == "0: undefined") && quantities.some(q => !q.locationId)) {
      if (quantities.some(q => !q.locationId && q.yard && q.assetTypeGroupId)) {
        total += quantities.filter(q => !q.locationId && q.yard && q.assetTypeGroupId).map(q => q.yard).reduce((partialSum, a) => partialSum + a, 0);
      }
      else {
        total += quantities.filter(q => !q.locationId && q.yard && !q.assetTypeGroupId).map(q => q.yard).reduce((partialSum, a) => partialSum + a, 0);
      }
    }
    else if (!this.selectedAssetLocationId || this.selectedAssetLocationId == "0: undefined") {
      if (quantities.some(q => q.yard && q.assetTypeGroupId)) {
        total += quantities.filter(q => q.yard && q.assetTypeGroupId).map(q => q.yard).reduce((partialSum, a) => partialSum + a, 0);
      }
      else {
        total += quantities.filter(q => q.yard && !q.assetTypeGroupId).map(q => q.yard).reduce((partialSum, a) => partialSum + a, 0);
      }
    }
    else {
      if (quantities.some(q => q.locationId == this.selectedAssetLocationId && q.yard && q.assetTypeGroupId)) {
        total += quantities.filter(q => q.locationId == this.selectedAssetLocationId && q.yard && q.assetTypeGroupId).map(q => q.yard).reduce((partialSum, a) => partialSum + a, 0);
      }
      else {
        total += quantities.filter(q => q.locationId == this.selectedAssetLocationId && q.yard && !q.assetTypeGroupId).map(q => q.yard).reduce((partialSum, a) => partialSum + a, 0);
      }
    }

    return total;
  }

  filteredAssetsDamaged() {
    return this.filteredAssets.filter(d => (d.damagedDate && !d.responseDate));
  }

  filteredAssetsDamagedOnLocation() {
    return this.filteredAssets.filter(d => (d.damagedDate && !d.responseDate && d.locationTypeClassification != LocationTypeClassification.Depot));
  }

  filteredAssetsYard() {
    return this.filteredAssets.filter(d => d.isYardBin && !(d.damagedDate && !d.responseDate));
  }

  filteredAssetsSurplus() {
    return this.filteredAssets.length - this.getYardTotal() - this.filteredAssetsDamaged().length + this.filteredAssetsDamagedOnLocation().length;
  }

  goToLocation(locationId: string) {
    this.router.navigate(this.getLocationNavigationCommands(locationId));
  }

  filterLocations(val: string): CustomerLocationDto[] {
    if (!this.locations) {
      return [];
    }

    return this.locations.filter(l => l.displayName.toLowerCase().includes(val.toLowerCase()));
  }

  onLocationSelect(option){
    this.router.navigate(this.getLocationNavigationCommands(option.id));
  }

  getLocationNavigationCommands(locationId: string): any[]{
    let location = this.locations.find(l => l.id == locationId);

    if (location && location.locationTypeClassification == LocationTypeClassification.Depot) {
      return ['/depot-details', {depotId: location.depotId, locationId: location.id}];
    }

    return [`/location/${location.id}`];
  }
}